import { EventEmitter, Injectable } from '@angular/core';

import { AppState } from '../app-state';

import { ScoutPointLocalPhoto, ScoutPointPhoto } from '../models/scout-task';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { BehaviorSubject, combineLatest, from, Subscription, throwError, timer } from 'rxjs';
import { ScoutPointPhotoDoc } from '../schema/scout-point-photo-schema';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { ScoutPointPhotoService } from './scout-point-photo.service';
import { ScoutPointLocalPhotoService } from './scout-point-local-photo.service';
import { ScoutPointLocalPhotoUrlService } from './scout-point-local-photo-url.service';
import { liveQuery, Subscription as DexieSubscription } from 'dexie';

export class UploadPhotoState {
  bProcessing = false
  bytesUploaded = 0
  percentComplete = 0
  uploadStartDate = ''
  errorMsg : string
}


export class UploadProgressSubject extends BehaviorSubject<UploadPhotoState>{
    
  constructor( public photoId: string, uploadState : UploadPhotoState) {
    super(uploadState)    
  }
}

export class UploadCompleteEvent {
  constructor( public photoDoc : ScoutPointPhotoDoc, public error: any) {    
  }
}

@Injectable({ providedIn: 'root' })
export class ScoutPointPhotoUploadService {

  static readonly MAX_UPLOAD_RETRY_CNT = 5
  static readonly UPLOAD_RETRY_INTERVAL_MINUTES_LIST = [ 0.25, 1, 5, 30, 60 ]
  static readonly MAX_CONCURRENT_UPLOAD_CNT = 2

  uploadNeededQuerySubscription : DexieSubscription
  uploadFailedSubscription : DexieSubscription
  uploadInProgressSubscription : DexieSubscription

  uploadNeededDocs : ScoutPointLocalPhoto[] = []
  errorPhotoDocs : ScoutPointLocalPhoto[] = []
  uploadingPhotoDocs : ScoutPointLocalPhoto[] = []

  uploadStartedEmitter = new EventEmitter<UploadProgressSubject>()
  uploadProgressSubjects : UploadProgressSubject[] = []

  uploadInProgressCntSubject = new BehaviorSubject<number>(0)
  uploadNeededCntSubject = new BehaviorSubject<number>(0)
  uploadFailedCntSubject = new BehaviorSubject<number>(0)
  
  uploadCompleteEmitter = new EventEmitter<UploadCompleteEvent>()

  subscriptions : Subscription[] = []
  

  constructor(
    protected appState: AppState,
    protected photoService: ScoutPointPhotoService,
    protected localPhotoService: ScoutPointLocalPhotoService,
    protected localPhotoUrlService: ScoutPointLocalPhotoUrlService) {
  }

  
  getDelayForRetryCntMs(retryCnt:number){
    const index = Math.min( ScoutPointPhotoService.UPLOAD_RETRY_INTERVAL_MINUTES_LIST.length-1,retryCnt-1)
    const minutes = ScoutPointPhotoService.UPLOAD_RETRY_INTERVAL_MINUTES_LIST[index]
    //console.log("Retry #" + retryCnt + ' waiting ' + minutes + ' minutes.')
    return minutes * 60 * 1000
  }

  uploadsEnabled = false

  start() {
    console.log('start()')

    const uploadsEnabledSubscription = combineLatest([
      this.appState.isOfflineSubject,
      this.appState.userSubject,
      this.appState.readySubject
    ]).pipe( 
      map( ([offline,user,ready]) => ready && !offline && !!user ),
      distinctUntilChanged()
    ).subscribe( uploadsEnabled => {
      this.uploadsEnabled = uploadsEnabled
      console.log('uploadsEnabled='+uploadsEnabled)
      if ( uploadsEnabled ) {
        this.startQueries()
      } else {
        this.stop()
      }
    })
    this.subscriptions.push(uploadsEnabledSubscription)          
  }


  stop() {
    console.log('stop()')
    if ( this.uploadNeededQuerySubscription ) {
      this.processingUploadDocs.length = 0
      this.uploadNeededQuerySubscription?.unsubscribe()
      this.uploadNeededQuerySubscription = null
    }
    if ( this.uploadInProgressSubscription ) {
      this.uploadInProgressSubscription?.unsubscribe()
      this.uploadInProgressSubscription = null
    }
    if ( this.uploadFailedSubscription ) {
      this.uploadFailedSubscription?.unsubscribe()
      this.uploadFailedSubscription = null
    }
  }


  async startQueries() {
    
    console.log('startQueries()')

    // clear out old errors and old uploads that were in progress
    try {
      let erroredFileDocs = await this.localPhotoService.dexieStore.where('uploadDate').equals('')
        .filter( doc => !!doc.errorMsg || !!doc.uploadStartDate).toArray()
      for( let doc of erroredFileDocs ) {
        console.log('Errored upload local doc: ' + doc?._id)
        //TODO use rxjs or promiseall
        await this.localPhotoService.update( doc, { uploadStartDate: '', retryCnt: 0, errorMsg: '' })
      }
    } catch ( error ) {
      console.log(error)
    }

    this.initUploadNeededSubscription()
    this.initUploadInProgressSubscription()
    this.initUploadFailedSubscription()
  }

  
  initUploadNeededSubscription() {
    this.uploadNeededQuerySubscription?.unsubscribe()
    
    const uploadNeeded$ = liveQuery( () => this.localPhotoService.where({ 
      uploadStartDate: '', 
      uploadDate: '', 
      errorMsg: ''
    }).toArray())
    this.uploadNeededQuerySubscription = uploadNeeded$.subscribe( 
      async uploadNeededDocs => {
          this.uploadNeededDocs = uploadNeededDocs
          this.uploadNeededCntSubject.next(this.uploadNeededDocs.length)
          if ( !this.appState.isOffline() && this.uploadNeededDocs.length > 0) {
            this.uploadNeededQuerySubscription?.unsubscribe()
            try {
              await this.processUploadNeededDocs() 
            } catch ( error ) {
              console.error(error)
              this.photoService.publishError(error,this)
            }
            this.initUploadNeededSubscription()
          }
      },
      error => {
        console.error('failed uploading local docs')
        console.error(error)
        this.initUploadNeededSubscription()
      }
    )
  }


  processingUploadDocs : ScoutPointLocalPhoto[] = []
  processingUploadCntEmitter = new EventEmitter<number>()


  
  processUploadNeededDocs = async () =>           {
    //console.log('processUploadNeededDocs() *** BEGIN ***')
    //console.log('\t    uploadNeededDocs: ' + this.uploadNeededDocs.length)
    //console.log('\tprocessingUploadDocs: ' + this.processingUploadDocs.length)
    
    while ( !this.appState.isOffline() && this.uploadNeededDocs.length > 0 ) {
      const localPhoto : ScoutPointLocalPhoto = this.uploadNeededDocs.shift()
      //console.log('processUploadNeededDocs() *** ' + localPhoto?.name + ' ***')
      if ( !localPhoto ) {
        continue
      }
      this.processingUploadDocs.push(localPhoto)
      this.processingUploadCntEmitter.next(this.processingUploadDocs.length)
      this.uploadNeededCntSubject.next(this.uploadNeededDocs.length)

      await this.photoService.beginPhotoTransaction(localPhoto._id)
      try{
        await this.localPhotoService.update( localPhoto, {uploadStartDate: this.photoService.nowTimeStamp()})
        const photoDoc : ScoutPointPhotoDoc  = await this.photoService.rxCollection.findOne(localPhoto._id).exec()
        const dataUrl = (await this.localPhotoUrlService.getById(localPhoto._id))?.url
        if ( !dataUrl ) {
          const msg = 'Invalid state.  Local photo meta data defined but no local photo URL data found'
          console.error(msg)
          await this.localPhotoService.update( localPhoto, {
            errorMsg: msg, 
            retryCnt: ScoutPointPhotoService.MAX_UPLOAD_RETRY_CNT // don't retry right away
          })
        } else {
          const photoFile : File = await this.photoService.dataUrlToFileObject(dataUrl, photoDoc.fileName, photoDoc.mimeType )
          const uploadReturned = (msg:string) => {
            //console.log('*** uploadPhotoFile() returned for ' + photoFileDoc.name + ' *** ' + msg)
            const preCnt = this.processingUploadDocs.length
            this.processingUploadDocs = this.processingUploadDocs.filter(d=>d._id!=localPhoto._id)
            if ( preCnt <= this.processingUploadDocs.length ) {
              console.warn('Failed removing uploaded photo from processingUploadDocs list')
              console.warn('photoFileDoc._id='+localPhoto._id)
              console.warn('processingUploadDocs: ' + JSON.stringify(this.processingUploadDocs.map(d=>d._id)))
          }
            this.processingUploadCntEmitter.next(this.processingUploadDocs.length)
          }
          from(this.uploadPhotoFile(photoFile, photoDoc, localPhoto, null, (progress)=>{})).subscribe(
            uploadedDoc => uploadReturned('OK'),
            error => {
              console.error(error)
              uploadReturned('ERROR')  
            }
          )
        }
      } catch ( error ) {
        this.photoService.publishError(error, this)              
      }      
      this.photoService.endPhotoTransaction(localPhoto._id)
      const maxProcessingCnt = ScoutPointPhotoUploadService.MAX_CONCURRENT_UPLOAD_CNT
      if ( this.processingUploadDocs.length >= maxProcessingCnt ) {
        console.log('processUploadNeededDocs() *** processingUploadCntMax WAIT ***')
        await this.processingUploadCntEmitter.pipe( filter(cnt=>cnt<maxProcessingCnt), take(1)).toPromise()
        console.log('processUploadNeededDocs() *** processingUploadCntMax CONTINUE ***')
      }
    }
    //console.log('processUploadNeededDocs() *** END ***')
  }


  initUploadInProgressSubscription() {
    this.uploadInProgressSubscription?.unsubscribe()
    this.uploadInProgressSubscription = liveQuery( () => this.localPhotoService.where({
      uploadDate: '',
      errorMsg: ''
    }).filter( doc => !!doc.uploadStartDate ).toArray() ).subscribe( 
      uploadingPhotoDocs => {
      this.uploadingPhotoDocs = uploadingPhotoDocs
      this.uploadInProgressCntSubject.next(this.uploadingPhotoDocs.length)
    })
  }


  initUploadFailedSubscription() {
    this.uploadFailedSubscription?.unsubscribe()
    this.uploadFailedSubscription = liveQuery( () => this.localPhotoService.dexieStore.where({
      uploadDate: '' }).filter( doc => !!doc.errorMsg ).toArray() ).subscribe( 
      errorPhotoDocs => {
        this.errorPhotoDocs = errorPhotoDocs
        this.uploadFailedCntSubject.next(this.errorPhotoDocs.length)
        if ( this.appState.isOffline() || !this.appState.getUser() ) { 
          return 
        }
        for ( let localPhoto of errorPhotoDocs ) {
          console.error('Photo upload error for ' + localPhoto.name + ', id=' + localPhoto._id + ', errorMsg=' + localPhoto.errorMsg )
          if ( localPhoto.retryCnt < ScoutPointPhotoService.MAX_UPLOAD_RETRY_CNT ) {
            const retryDelayMs = this.getDelayForRetryCntMs(localPhoto.retryCnt)
            timer( retryDelayMs ).subscribe( async () => {
              try{
                if ( !await this.localPhotoService.getById(localPhoto._id) ) {
                  return // already removed
                }
                await this.photoService.beginPhotoTransaction(localPhoto._id)                 
                if ( !await this.localPhotoService.getById(localPhoto._id) ) {
                  return // already removed (after pseudo transaction started)
                }
                const bErrorResolved = [null,''].includes(localPhoto.errorMsg)
                if ( !bErrorResolved ) {
                  console.log('latestPhotoFileDoc: ' + localPhoto?._id)
                  await this.localPhotoService.update( localPhoto, {
                    // put doc in state so it will get picked up by uploadNeededQuerySubscription
                    errorMsg: '',
                    uploadStartDate: ''
                  })
                }
              } catch ( error ) {
                console.error('Failed rescheduling upload for photo ' + localPhoto._id + '. ' + error?.message)
                throw error
              } finally {
                this.photoService.endPhotoTransaction(localPhoto._id)
              }
            })
          } else {
            console.error('Max resends for photo exceeded.  ' + localPhoto.name + ', id=' + localPhoto._id )
          }
        }
      }
    )

  }


  private async uploadPhotoFile( 
      photoFile : File, 
      photoDoc : ScoutPointPhotoDoc,
      localPhoto : ScoutPointLocalPhoto,
      photoMetadata : ScoutPointPhoto,
      progressSetter : (progress:number)=>void) : Promise<ScoutPointPhotoDoc> {

    console.log('uploadPhotoFile() ' + photoFile?.name)

    let uploadState = new UploadPhotoState()
    uploadState.bProcessing = true

    let uploadProgressSubject = new UploadProgressSubject(photoDoc._id,uploadState)
    this.uploadProgressSubjects.push(uploadProgressSubject)
    this.uploadStartedEmitter.next(uploadProgressSubject)

    const uploadFailed = (uploadState: UploadPhotoState, msg : string) => {
      uploadState.errorMsg = msg
      uploadState.percentComplete = 0
      uploadState.bProcessing = false
      uploadProgressSubject.next(uploadState)
      uploadProgressSubject.complete()
      this.uploadProgressSubjects = this.uploadProgressSubjects.filter(emitter=>emitter.photoId!=photoDoc._id)
      this.uploadCompleteEmitter.next(new UploadCompleteEvent(photoDoc,msg))
    }

    return new Promise( async (resolve, reject) => {
      if ( !localPhoto ) {
        const msg = 'Invalid state.  localPhotoDoc not set for ' + photoFile?.name
        console.warn(msg)
        reject(msg)
      }
      try {
        //console.log('fileDoc.uploadStartDate: ' + fileDoc?.uploadStartDate + ' fileDoc null: ' + (!fileDoc))
        await this.localPhotoService.update( localPhoto, {
          uploadStartDate: this.photoService.nowTimeStamp(),
          errorMsg: ''
        })
        uploadState.uploadStartDate = localPhoto.uploadStartDate
        uploadProgressSubject.next(uploadState)    
      } catch ( error ) {
        console.error(error)
        const msg = 'Upload failed to start because rxdb photo uploadStartDate update failed'
        uploadFailed(uploadState,msg)
        reject( new Error(msg) )
      }
      let metadata : any = {}
      if ( photoMetadata ) {
        Object.assign(metadata,photoMetadata)
      } else {
        metadata = photoDoc.toJSON()
      }
      metadata.uploadDate = this.photoService.nowTimeStamp()
      this.uploadScoutRefPhotoFile(photoDoc._id,metadata,photoFile).subscribe(
        async (event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.Sent:
              //console.log('uploadPhotoFile() HttpEventType.Sent');
              break;
            case HttpEventType.ResponseHeader:
              //console.log('uploadPhotoFile() HttpEventType.ResponseHeader');
              break;
            case HttpEventType.UploadProgress:
              let progress = Math.round(event.loaded / event.total * 100)
              //console.log( 'uploadPhotoFile() HttpEventType.UploadProgress - loaded: ' +  event.loaded + ', total: ' + event.total + ' ' + progress + '% of ' + localPhotoDoc.name)
              progressSetter(progress);
              uploadState.bytesUploaded = event.loaded
              uploadState.percentComplete = progress
              uploadProgressSubject.next(uploadState)
              break;
            case HttpEventType.Response:
              if ( event.status != 200 ) {
                const msg = 'Server: ' + event.statusText
                console.error('uploadPhotoFile() HttpEventType.Response: ' + msg)
                progressSetter(0)
                await this.localPhotoService.update( localPhoto, {
                  errorMsg: msg,
                  retryCnt: localPhoto.retryCnt + 1
                })
                this.photoService.publishError('Failed uploading "' + photoFile.name + '" (' + msg + ')', this)
                uploadFailed(uploadState,msg)
                reject(new Error(msg))
              } else {
                console.log('Image saved remotely: ' + photoFile.name);
                this.uploadProgressSubjects = this.uploadProgressSubjects.filter(emitter=>emitter.photoId!=photoDoc._id)
                const uploadDate = this.photoService.nowTimeStamp()
                this.photoService.publishSuccess('Uploaded ' + photoDoc.fileName, this, 5)
                try{
                  await photoDoc.atomicUpdate((doc)=>{
                    this.photoService.assignWhoAndWhen(doc)
                    doc.uploadDate = uploadDate
                    return doc
                  })
                  try{
                    await this.localPhotoService.update( localPhoto, {
                      uploadDate,
                      retryCnt: 0
                    })
                  } catch ( error ) {
                    const msg = 'Uploaded photo but failed updating local rxdb photo data in browser (check logs)'
                    console.error(error)
                    this.photoService.publishError( error, this)
                  }                
                } catch ( error ) {
                  const msg = 'Uploaded photo but failed updating shared rxdb photo data in browser (check logs)'
                  console.error(error)
                  this.photoService.publishError( error, this)
                }
                uploadState.bProcessing = false
                uploadProgressSubject.next(uploadState)
                this.uploadCompleteEmitter.next(new UploadCompleteEvent(photoDoc,null))
                setTimeout(() => {
                  uploadProgressSubject.complete()
                  progressSetter(0)
                }, 500);
                resolve(photoDoc)
              }
          }
        }, 
        async err => {
          let msg = this.photoService.extractErrorMsg(err)
          if ( !msg ) { 
            msg = 'Photo upload failed for ' + photoDoc.fileName
          }              
          //this.publishError(msg,this)
          try{
            await this.localPhotoService.update( localPhoto, {
              errorMsg: msg,
              retryCnt: localPhoto.retryCnt + 1
              
            })
          } catch ( error ) {
            console.error(error.message)
          }
          progressSetter(0)
          uploadFailed(uploadState,msg)
          reject(err)
        }
      )
    })
  }


  uploadScoutRefPhotoFile(photoFileId: string, photoMetadata: any, photoFile: File) {
    if (!photoFileId) {
      const errMsg = 'uploadScoutRefPhotoFile photoFileId required.'
      console.error(errMsg)
      return throwError(errMsg)
    } else if (!photoFile) {
      return throwError('Upload scout ref file input missing (form data of local file).')
    }   
    var formData: any = new FormData();
    formData.append("photoId", photoFileId)
    formData.append("metadata", JSON.stringify(photoMetadata))
    formData.append("file", photoFile);
    return this.photoService.restClient.http.post(this.photoService.restClient.urlPath + "/ref-file", formData, {
      reportProgress: true,
      observe: 'events'
    })
  }
}